// LICENSE PROTECTED SOFTWARE
import React, { useEffect, useState } from 'react';
import { AlertTriangle, Clock, ShieldCheck, Plus, FileWarning, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';
import { toast } from 'sonner';

const SEVERITY_COLORS = {
  critical: 'bg-red-600 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-amber-400 text-slate-900',
  low: 'bg-slate-300 text-slate-800',
};

const DATA_TYPES = [
  { value: 'tc_kimlik', label: 'T.C. Kimlik' },
  { value: 'name', label: 'Ad Soyad' },
  { value: 'email', label: 'E-posta' },
  { value: 'phone', label: 'Telefon' },
  { value: 'address', label: 'Adres' },
  { value: 'birth_date', label: 'Doğum Tarihi' },
  { value: 'password', label: 'Parola Hash' },
  { value: 'session_token', label: 'Oturum Tokeni' },
];

const BREACH_TYPES = [
  { value: 'unauthorized_access', label: 'Yetkisiz Erişim' },
  { value: 'data_leak', label: 'Veri Sızıntısı' },
  { value: 'phishing', label: 'Phishing/Sosyal Mühendislik' },
  { value: 'lost_device', label: 'Cihaz Kaybı' },
  { value: 'malware', label: 'Kötü Amaçlı Yazılım' },
  { value: 'insider', label: 'İç Tehdit' },
  { value: 'other', label: 'Diğer' },
];

export default function DataBreachPage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, open: 0, kvkk_overdue: 0 });
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    title: '', description: '', severity: 'high', breach_type: '',
    affected_user_count: 0, affected_data_types: [],
    discovered_at: new Date().toISOString().slice(0, 16),
    root_cause: '', remediation_actions: '',
  });

  // Guvenli hata mesaji cikarici (Pydantic 422 array of dict, string, obj)
  const errMsg = (err, fallback = 'Hata') => {
    const d = err?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d) && d.length > 0) return d.map(x => x?.msg || JSON.stringify(x)).join('; ');
    if (d) return JSON.stringify(d);
    return err?.message || fallback;
  };

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get('/data-breach');
      setItems(r.data.items || []);
      setStats(r.data.stats || {});
    } catch (e) { toast.error('Olaylar yüklenemedi: ' + errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/data-breach', { ...form, discovered_at: new Date(form.discovered_at).toISOString() });
      toast.success('Veri ihlali olayı kayıt altına alındı');
      setShowForm(false);
      setForm({ ...form, title: '', description: '', root_cause: '', remediation_actions: '' });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const markReported = async (id, field) => {
    if (!window.confirm(`${field === 'kvkk' ? 'KVKK Kurumu' : 'Etkilenen kullanıcılar'} bildirim tarihini şu an olarak işaretleyelim mi?`)) return;
    try {
      const payload = field === 'kvkk' ? { reported_to_kvkk_at: new Date().toISOString(), status: 'reported' }
                                       : { reported_to_users_at: new Date().toISOString() };
      await api.put(`/data-breach/${id}`, payload);
      toast.success('Bildirim tarihi kaydedildi');
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/data-breach/${id}`, { status });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="data-breach-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <FileWarning className="w-6 h-6 text-red-600"/> KVKK Veri İhlali Bildirimi
          </h1>
          <p className="text-sm text-slate-500 mt-1">KVKK Madde 12/5 — Tespit edilen ihlal en geç 72 saat içinde Kuruma bildirilmelidir.</p>
        </div>
        <Button onClick={() => setShowForm(true)} data-testid="new-breach-btn" className="bg-red-600 hover:bg-red-700 text-white">
          <Plus className="w-4 h-4 mr-1"/> Yeni İhlal Kaydı
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card><CardContent className="p-5"><p className="text-xs text-slate-500 uppercase">Toplam</p><p className="text-3xl font-semibold mt-1">{stats.total}</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-xs text-slate-500 uppercase">Açık Vakalar</p><p className="text-3xl font-semibold mt-1 text-amber-600">{stats.open}</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-xs text-slate-500 uppercase">72 Saat Geçti (KVKK Bekliyor)</p><p className="text-3xl font-semibold mt-1 text-red-600">{stats.kvkk_overdue}</p></CardContent></Card>
      </div>

      {loading ? <p className="text-sm text-slate-500">Yükleniyor...</p> :
       items.length === 0 ?
        <Card><CardContent className="p-12 text-center"><ShieldCheck className="w-12 h-12 text-green-500 mx-auto mb-3"/><p className="text-slate-600">Henüz kayıtlı bir veri ihlali olayı bulunmamaktadır.</p></CardContent></Card>
        :
        <div className="space-y-3">
          {items.map(i => (
            <Card key={i.id} data-testid={`incident-${i.incident_no}`}>
              <CardContent className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs px-2 py-1 bg-slate-100 rounded">{i.incident_no}</span>
                    <Badge className={SEVERITY_COLORS[i.severity]}>{i.severity?.toUpperCase()}</Badge>
                    <Badge variant="outline">{i.status}</Badge>
                  </div>
                  {i.kvkk_deadline_hours_left !== null && (
                    <div className={`flex items-center gap-1 text-xs font-medium ${i.kvkk_deadline_passed ? 'text-red-600' : 'text-amber-600'}`}>
                      <Clock className="w-3 h-3"/>
                      {i.kvkk_deadline_passed
                        ? `${Math.abs(i.kvkk_deadline_hours_left)} saat GECİKTİ`
                        : `KVKK bildirimine ${i.kvkk_deadline_hours_left} saat`}
                    </div>
                  )}
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">{i.title}</h3>
                <p className="text-sm text-slate-600 mb-3 whitespace-pre-wrap">{i.description}</p>
                <div className="text-xs text-slate-500 grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                  <div><span className="font-medium">Tespit:</span> {new Date(i.discovered_at).toLocaleString('tr-TR')}</div>
                  <div><span className="font-medium">Etkilenen:</span> {i.affected_user_count} kullanıcı</div>
                  <div><span className="font-medium">Tip:</span> {i.breach_type || '-'}</div>
                  <div><span className="font-medium">Kayıt:</span> {i.created_by_name}</div>
                </div>
                {Array.isArray(i.affected_data_types) && i.affected_data_types.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {i.affected_data_types.map(dt => <Badge key={dt} variant="secondary" className="text-xs">{dt}</Badge>)}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-slate-100">
                  {!i.reported_to_kvkk_at && (
                    <Button size="sm" variant="outline" onClick={() => markReported(i.id, 'kvkk')} data-testid={`kvkk-report-${i.incident_no}`}>
                      <AlertTriangle className="w-3 h-3 mr-1"/> KVKK'ya Bildirildi
                    </Button>
                  )}
                  {i.reported_to_kvkk_at && (
                    <Badge className="bg-green-100 text-green-800 border border-green-200">
                      KVKK: {new Date(i.reported_to_kvkk_at).toLocaleDateString('tr-TR')}
                    </Badge>
                  )}
                  {!i.reported_to_users_at && (
                    <Button size="sm" variant="outline" onClick={() => markReported(i.id, 'users')} data-testid={`users-report-${i.incident_no}`}>
                      Kullanıcılara Bildirildi
                    </Button>
                  )}
                  {i.reported_to_users_at && (
                    <Badge className="bg-green-100 text-green-800 border border-green-200">
                      Kullanıcı: {new Date(i.reported_to_users_at).toLocaleDateString('tr-TR')}
                    </Badge>
                  )}
                  <div className="ml-auto">
                    <Select value={i.status} onValueChange={v => updateStatus(i.id, v)}>
                      <SelectTrigger className="w-44 h-8 text-xs"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Açık</SelectItem>
                        <SelectItem value="investigating">İnceleniyor</SelectItem>
                        <SelectItem value="reported">Bildirildi</SelectItem>
                        <SelectItem value="resolved">Çözüldü</SelectItem>
                        <SelectItem value="closed">Kapatıldı</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      }

      {showForm && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" data-testid="breach-form-modal">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Yeni Veri İhlali Olayı</h2>
                <button onClick={() => setShowForm(false)}><X className="w-5 h-5 text-slate-400"/></button>
              </div>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>Başlık * <span className="text-xs text-slate-400">(en az 5 karakter)</span></Label>
                  <Input required minLength={5} maxLength={500} value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="breach-title-input" placeholder="Örn: Yetkisiz veritabanı erişim girişimi"/>
                </div>
                <div>
                  <Label>Açıklama * <span className="text-xs text-slate-400">(en az 20 karakter)</span></Label>
                  <Textarea required minLength={20} rows={4} value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="breach-description-input" placeholder="Olay hakkında detaylı açıklama..."/>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Önem *</Label>
                    <Select value={form.severity} onValueChange={v => setForm({...form, severity: v})}>
                      <SelectTrigger data-testid="breach-severity-select"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="critical">Kritik</SelectItem>
                        <SelectItem value="high">Yüksek</SelectItem>
                        <SelectItem value="medium">Orta</SelectItem>
                        <SelectItem value="low">Düşük</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>İhlal Tipi</Label>
                    <Select value={form.breach_type} onValueChange={v => setForm({...form, breach_type: v})}>
                      <SelectTrigger data-testid="breach-type-select"><SelectValue placeholder="Seçin"/></SelectTrigger>
                      <SelectContent>
                        {BREACH_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Tespit Tarihi *</Label>
                    <Input type="datetime-local" required value={form.discovered_at} onChange={e => setForm({...form, discovered_at: e.target.value})} data-testid="breach-discovered-input"/>
                  </div>
                  <div>
                    <Label>Etkilenen Kullanıcı Sayısı</Label>
                    <Input type="number" min={0} value={form.affected_user_count} onChange={e => setForm({...form, affected_user_count: parseInt(e.target.value) || 0})} data-testid="breach-affected-input"/>
                  </div>
                </div>
                <div>
                  <Label>Etkilenen Veri Türleri</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                    {DATA_TYPES.map(dt => (
                      <label key={dt.value} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={form.affected_data_types.includes(dt.value)}
                          onChange={e => {
                            const set = new Set(form.affected_data_types);
                            if (e.target.checked) set.add(dt.value); else set.delete(dt.value);
                            setForm({...form, affected_data_types: Array.from(set)});
                          }}/>
                        {dt.label}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Kök Neden (opsiyonel)</Label>
                  <Textarea rows={2} value={form.root_cause} onChange={e => setForm({...form, root_cause: e.target.value})}/>
                </div>
                <div>
                  <Label>Alınan Önlemler (opsiyonel)</Label>
                  <Textarea rows={2} value={form.remediation_actions} onChange={e => setForm({...form, remediation_actions: e.target.value})}/>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>İptal</Button>
                  <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white" data-testid="breach-submit-btn">Olayı Kaydet</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
